import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  CardBodyComponent,
  CardComponent,
  CardDescriptionComponent,
  CardHeaderComponent,
  CardLeadingComponent,
  CardTitleComponent,
  CurrencyFormatPipe,
  EmptyStateComponent,
  IconComponent,
  InterpolatePipe,
  LoadingSkeletonComponent,
  ModalStore,
  SOFTLINE_FEATURE_MODAL,
  TranslatePipe } from '@softline/ui-core';
import { BarChartModule, BarHorizontalComponent } from '@swimlane/ngx-charts';
import { addDays, DownloadStore, getIsoWeek, Message, parseIso8601, saveAs, SOFTLINE_FEATURE_DOWNLOAD, Store, toIsoDate } from '@softline/core';
import { BarChartData } from '../../types/chart-data.model';
import { OffenerPosten } from '../../types/offener-posten';
import { showRequestErrors } from '@softline/application';
import { SOFTLINE_API_JOPOSTEN_BELEG_DOWNLOAD } from '../../offene-posten.api';
import { OffenePostenTableDialogComponent } from '../../dialogs/offene-posten-table-dialog/offene-posten-table-dialog.component';

const OP_ZEITRAEUME = {
  allPerToday: {
    label: 'Alle per Heute' },
  remainingWeek: {
    label: 'Restliche KW' },
  nextWeek: {
    label: 'Nächste KW' },
  allAfterNextWeek: {
    label: 'Später' } };

@Component({
  selector: 'soft-op-diagram-tabelle-view',
  imports: [CommonModule, BarChartModule,
    CurrencyFormatPipe,
    TranslatePipe,
    InterpolatePipe,
    EmptyStateComponent,
    LoadingSkeletonComponent,
    CardComponent,
    CardHeaderComponent,
    CardLeadingComponent,
    CardTitleComponent,
    CardDescriptionComponent,
    CardBodyComponent,
    IconComponent],
  templateUrl: './diagram-tabelle-view.component.html',
  styleUrl: './diagram-tabelle-view.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslatePipe] })
export class DiagramTabelleViewComponent {
  private readonly datePipe = inject(DatePipe);

  private;

  offenePosten = input<OffenerPosten[]>([]);
  title = input('');
  subtitle = input('');
  loading = input(false);
  error = input<Message<object> | null>(null);
  invertValues = input(false);
  showExtraColumn = input(false);
  color = input<string>('var(--colorPrimary500)');

  readonly chartColorScheme = computed(() => {
    return { domain: [this.color()] };
  });

  @ViewChild('chart') readonly chart?: BarHorizontalComponent;

  readonly selectedTabIndex = signal<number>(0);

  readonly tabelleDatumsbereich = signal<{
    from: Date | null;
    to: Date | null;
  }>({
    from: null,
    to: null });

  readonly tableData = computed(() => {
    const offenePosten = this.offenePosten();
    const datumsbereich = this.tabelleDatumsbereich();

    if (offenePosten?.length < 1) return [];

    const vondatum = datumsbereich?.from;
    const bisdatum = datumsbereich?.to;

    if (!vondatum || !bisdatum) return offenePosten;

    return offenePosten
      .filter((o) => this.isBetweenDate(o.faelldatum, vondatum, bisdatum))
      .sort((a, b) =>
        a.faelldatum > b.faelldatum ? 1 : b.faelldatum > a.faelldatum ? -1 : 0
      );
  });

  readonly chartData = computed(() => {
    const offenePosten = this.offenePosten();
    const invertValues = this.invertValues();
    const opHeute = {
      name:
        this.translatePipe.transform('#OFFENE_POSTEN.DIAGRAMM.LABEL_ALLE_PER_HEUTE') ??
        '',
      value: 0 };
    const opWoche = {
      name:
        this.translatePipe.transform('#OFFENE_POSTEN.DIAGRAMM.LABEL_DERZEITIGE_KW') ?? '',
      value: 0 };
    const opNaechsteWoche = {
      name:
        this.translatePipe.transform('#OFFENE_POSTEN.DIAGRAMM.LABEL_NAECHSTE_KW') ?? '',
      value: 0 };
    const opSpaeter: BarChartData = {
      name: this.translatePipe.transform('#OFFENE_POSTEN.DIAGRAMM.LABEL_SPAETER') ?? '',
      value: 0 };

    const today = localMidnight(new Date());
    const thisWeekStart = startOfIsoWeekDate(new Date());
    const nextWeekStart = shiftDays(thisWeekStart, 7);
    const weekAfterNextStart = shiftDays(thisWeekStart, 14);

    for (const item of offenePosten) {
      const dueTime = parseIso8601(item.faelldatum);
      const due = dueTime !== null ? localMidnight(new Date(dueTime)) : null;
      const saldo = item.saldo * (invertValues ? -1 : 1);

      if (due && due.getTime() === today.getTime()) opHeute.value += saldo;
      else if (due && due >= thisWeekStart && due < nextWeekStart && due > today)
        opWoche.value += saldo;
      else if (due && due >= nextWeekStart && due < weekAfterNextStart)
        opNaechsteWoche.value += saldo;
      else if (due && due >= weekAfterNextStart) opSpaeter.value += saldo;
      else opHeute.value += saldo;
    }

    return [opHeute, opWoche, opNaechsteWoche, opSpaeter];
  });

  readonly faelligkeitenInTotal = computed(() => {
    const sum = this.offenePosten().reduce((sum, value) => sum + value.saldo, 0);
    return this.invertValues() ? sum * -1 : sum;
  });

  constructor(
    private store: Store,
    private translatePipe: TranslatePipe,
    private currencyFormatPipe: CurrencyFormatPipe
  ) {}

  readonly formatDataLabel = (value: string) => {
    return (
      this?.currencyFormatPipe?.transform(value, 'EUR', 'symbol', 'before', '') ?? value
    );
  };

  // required to fix a glitch in the chart when switching between chart and table (tabs)
  onChartActivate(): void {
    setTimeout(() => this.chart?.update(), 100);
  }

  async onSelect(event: BarChartData): Promise<void> {
    let weekNumber;
    let isThisWeek = false;

    if (this.matches(event.name, OP_ZEITRAEUME.allPerToday)) {
      weekNumber = getIsoWeek(toIsoDate(new Date()));
      isThisWeek = true;
    } else if (this.matches(event.name, OP_ZEITRAEUME.remainingWeek)) {
      weekNumber = getIsoWeek(toIsoDate(new Date()));
      isThisWeek = false;
    } else if (this.matches(event.name, OP_ZEITRAEUME.nextWeek)) {
      weekNumber = getIsoWeek(addDays(toIsoDate(new Date()), 7));
      isThisWeek = false;
    } else {
      weekNumber = +event.name.split(' ')[1];
      isThisWeek = false;
    }

    console.log('week Number:', weekNumber);

    if (!weekNumber || isNaN(weekNumber)) return;

    const year = new Date().getFullYear();
    const weekStart = isoWeekStartDate(year, weekNumber);
    const weekEnd = isoWeekEndDate(weekStart);

    let startDate: Date;
    let endDate: Date;

    if (isThisWeek) {
      const today = this.resetHoursToStartOfDay();

      const alleHeuteFaellig = this.offenePosten()
        .filter((a) => this.resetHoursToStartOfDay(a.faelldatum) <= today)
        .sort((a, b) => (b.faelldatum > a.faelldatum ? 1 : -1));

      startDate = localMidnight(
        new Date(
          parseIso8601(alleHeuteFaellig[alleHeuteFaellig.length - 1].faelldatum) ?? NaN
        )
      );
      endDate = localMidnight(new Date(parseIso8601(alleHeuteFaellig[0].faelldatum) ?? NaN));
    } else if (event.name === 'Restliche KW') {
      startDate = localMidnight(shiftDays(new Date(), 1));
      endDate = weekEnd;
    } else {
      startDate = weekStart;
      endDate = weekEnd;
    }

    this.tabelleDatumsbereich.set({ from: startDate, to: endDate });
    this.selectedTabIndex.set(1);

    await this.openDetails({
      from: startDate,
      to: endDate,
      offenePosten: this.tableData() });
  }

  async showOffenePostenDialog(): Promise<void> {
    await this.openDetails({
      offenePosten: this.offenePosten() });
  }

  async openDetails({
    from,
    to,
    offenePosten }: {
    from?: Date;
    to?: Date;
    offenePosten: OffenerPosten[];
  }): Promise<void> {
    await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open(), {
      component: OffenePostenTableDialogComponent,
      dismiss: true,
      data: {
        from,
        to,
        offenePosten,
        showExtraColumn: this.showExtraColumn() } });
  }

  async downloadFile(op: OffenerPosten): Promise<void> {
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_DOWNLOAD,
        DownloadStore.actions.download,
        {
          filename: `OP_${op.symbol.symbol}_${op.belegnummer}.pdf`,
          location: {
            path: SOFTLINE_API_JOPOSTEN_BELEG_DOWNLOAD,
            pathParams: {
              idjoposten: op.id } } }
      );

      saveAs(result.content, result.filename);
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  private resetHoursToStartOfDay(dateString?: string): Date {
    const date = dateString ? new Date(dateString) : new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private isBetweenDate(date: string, from: Date, to: Date): boolean {
    const time = new Date(date).getTime();
    return time > from.getTime() && time < to.getTime();
  }

  private matches(
    label: string,
    option: (typeof OP_ZEITRAEUME)[keyof typeof OP_ZEITRAEUME]
  ): boolean {
    return label.toLowerCase() === option.label.toLowerCase();
  }
}

function localMidnight(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

function shiftDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

// Monday 00:00 (local) of the week containing the given date
function startOfIsoWeekDate(date: Date): Date {
  const result = localMidnight(date);
  result.setDate(result.getDate() - ((result.getDay() + 6) % 7));
  return result;
}

// Monday 00:00 (local) of ISO week `week` in `year`
function isoWeekStartDate(year: number, week: number): Date {
  const jan4 = new Date(year, 0, 4);
  return new Date(year, 0, 4 - ((jan4.getDay() + 6) % 7) + (week - 1) * 7);
}

// Sunday 23:59:59.999 (local) of the week starting at `weekStart`
function isoWeekEndDate(weekStart: Date): Date {
  return new Date(
    weekStart.getFullYear(),
    weekStart.getMonth(),
    weekStart.getDate() + 6,
    23, 59, 59, 999
  );
}
